import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FetchError, GetIssuesDto, IssueDto, queryKeys, useAccessToken, UserDto } from "../../..";
import { configuration } from "../../../../configuration";
import { useSignalR } from "../../../../hooks";
import { optimisticallyUpdateQueryData } from "../../utilities";

type Variables = {
  proposalGuid: string;
  issueGuid: string;
  issueCreatedByUser: UserDto;
  teamSlug?: string;
};

const useDeleteProposalRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function deleteProposalRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();

    const response = await fetch(`${configuration.apiRootUrl}/proposals/${variables.proposalGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "delete",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { deleteProposalRequest };
};

const useDeleteProposal = () => {
  const { deleteProposalRequest } = useDeleteProposalRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: deleteProposalRequest,
    onMutate: async (variables) => {
      let cachedTeamIssuesData: GetIssuesDto | undefined;
      let cachedUserIssuesData: GetIssuesDto | undefined;
      let cachedIssueData: IssueDto | undefined;

      if (variables.teamSlug) {
        cachedTeamIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          (cachedData) => ({
            issues: cachedData.issues.map((issue) => {
              if (issue.guid === variables.issueGuid) {
                return {
                  ...issue,
                  proposalCount: issue.proposalCount > 0 ? issue.proposalCount - 1 : 0,
                };
              }
              return issue;
            }),
          })
        );
      }

      cachedUserIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(undefined, variables.issueCreatedByUser.userId),
        (cachedData) => ({
          issues: cachedData.issues.map((issue) => {
            if (issue.guid === variables.issueGuid) {
              return {
                ...issue,
                proposalCount: issue.proposalCount > 0 ? issue.proposalCount - 1 : 0,
              };
            }
            return issue;
          }),
        })
      );

      cachedIssueData = await optimisticallyUpdateQueryData<IssueDto>(
        queryClient,
        queryKeys.issues.getIssueDetail(variables.issueGuid),
        (cachedData) => ({
          proposals: cachedData.proposals.filter((proposal) => proposal.guid !== variables.proposalGuid),
        })
      );

      return { cachedTeamIssuesData, cachedUserIssuesData, cachedIssueData };
    },
    onSuccess: (_data, variables) => {
      if (variables.teamSlug) {
        signalR.sendRefetchTeamIssues(variables.teamSlug);
      }
      signalR.sendRefetchIssue(variables.issueGuid);
      signalR.sendRefetchUserIssues(variables.issueCreatedByUser.userId);
      signalR.sendRefetchComments(variables.issueGuid);
    },
    onError: (_error, variables, context) => {
      if (variables.teamSlug) {
        queryClient.setQueryData(
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          context?.cachedTeamIssuesData
        );
      }
      queryClient.setQueryData(
        queryKeys.issues.getIssues(undefined, variables.issueCreatedByUser.userId),
        context?.cachedUserIssuesData
      );
      queryClient.setQueryData(queryKeys.issues.getIssueDetail(variables.issueGuid), context?.cachedIssueData);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.teamSlug, undefined));
      queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, variables.issueCreatedByUser.userId));
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
      queryClient.invalidateQueries(queryKeys.issues.getIssueFeed(variables.issueGuid));
    },
  });
};

export { useDeleteProposal };
